// ─────────────────────────────────────────────
//  BoardPage — Main OR board, real-time from Firestore
//  Light token theme (D-13). My Rooms / Full Board role tabs (D-05).
//  RoomCard / BreakStatusRow are imported from the component library (02-01).
// ─────────────────────────────────────────────
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useRooms } from "../hooks/useRooms";
import { useStaff } from "../hooks/useStaff";
import { useAlertCenter } from "../context/AlertContext";
import { seedDatabase } from "../firebase/seed";
import { normalizeStatus } from "../domain/status";
import { useLocations } from "../hooks/useLocations";
import { useFacility } from "../hooks/useFacility";
import { useFacilities } from "../hooks/useFacilities";
import { facilityLabel, shortFacilityLabel } from "../domain/facilities";
import { useDailyRoster } from "../hooks/useDailyRoster";
import { useDayKey } from "../hooks/useDayKey";
import useRoomCases from "../hooks/useRoomCases";
import { listenToAssignments, setDailyRoster, setRosterMemberDeparted, removeRosterMember, updateAssignmentRow, listenToTransfers, updateTransfer, syncMdCallRemoval, syncMdCallRestore, syncMdCallConfirmHome } from "../firebase/firestore";
import { assignmentForRoom, hasAssignmentData } from "../domain/gridTemplate";
import { occupantRowForRoom, nowMinutes } from "../domain/roomOccupant";
import { rowClaimsAt } from "../domain/timeline";
import { coverageStatus } from "../domain/coverage";
import { filterActiveStaff } from "../domain/departure";
import { myRoomAssignments, userStaffIds } from "../domain/myRooms";
import { caseWriterMode, currentAttendingId } from "../domain/caseRole";
import { canStampCasePhases, isRoomTeamMode } from "../domain/casePhase";
import useCasePhases from "../hooks/useCasePhases";
import { testDoctors, testProviders } from "../config/testUsers";
import { groupRoomsByLocation, canonicalKey, labelFor, isTbdRoom } from "../domain/locations";
import { countRoomsSupervisedBy } from "../domain/alerts";
import { buildMdCallList, buildCrnaShiftGroups, removedSupervisorForRoom, resolveMdCallStaff } from "../domain/tvPanels";
import { useBroadcasts } from "../hooks/useBroadcasts";
import { TvCallPanel, TvCrnaPanel, TvBroadcastPanel } from "../components/TvPanels";
import VoiceControl from "../components/VoiceControl";
import { useAicByFacility } from "../hooks/useAicByFacility";
import { pickTvColumns } from "../domain/tvLayout";
import { nextDayKey } from "../domain/importDate";
import { useIsMobile } from "../hooks/useIsMobile";
import { useViewport, tvScaleForViewport } from "../hooks/useViewport";
import { RoomCard, RoleTab, AssignmentModal, BoardAlertControls, Avatar, Modal, Button, SignOutButton } from "../components";
import { resolveFacilityRules, resolveFacilityStatus } from "../domain/facilityRules";
import IncomingAlertPopup from "../components/IncomingAlertPopup";
import NavMenu from "../components/NavMenu";
import BoardSwitcher from "../components/controlPanel/BoardSwitcher";
import { isStandingOnCall } from "../domain/timeline";
import BenchAssignFlow from "../components/BenchAssignFlow";
import AicHandoffModal from "../components/AicHandoffModal";
import DoctorAssignFlow from "../components/DoctorAssignFlow";
import CrossFacilityPicker from "../components/CrossFacilityPicker";
import tokens from "../theme/tokens";
import { onCallChipLabel, onCallChipTitle } from "../domain/onCallChip";

const { color, space, radius, shadow, font } = tokens;

// Harmonic, DELIBERATELY MUTED location tints — one per OR location so the
// board reads at a glance which group a room belongs to. Each entry:
// { bg, border, head } (panel wash / edge / heading accent). Low-chroma washes
// (clinical-calm): locations stay tellable apart, but saturated color is
// reserved for STATUS and ALERTS — on the old candy palette the one red room
// didn't pop. Hues are ordered so consecutive locations land far apart.
const LOCATION_PALETTE = [
  { bg: "#e8ecf8", border: "#c6d0ec", head: "#3849b8" }, // indigo
  { bg: "#e4f1ea", border: "#bcdccb", head: "#0c7a52" }, // emerald
  { bg: "#f6efdd", border: "#e5d5a8", head: "#8a5c10" }, // amber
  { bg: "#f7e9ed", border: "#eac8d2", head: "#a83354" }, // rose
  { bg: "#e4eff5", border: "#bcd9e8", head: "#0e6f8a" }, // sky
  { bg: "#ede9f7", border: "#d4c9ee", head: "#6b46c1" }, // violet
  { bg: "#edf2e1", border: "#d3e3b7", head: "#4d7c0f" }, // lime
  { bg: "#f7ede1", border: "#ead0b3", head: "#a2481a" }, // orange
];

// Assign by the location's board ORDER when known so adjacent locations get
// far-apart colours; fall back to a stable hash of the key (custom locations,
// or when no index is given) so a location keeps the same tint.
function locationTint(key, index) {
  if (Number.isInteger(index) && index >= 0) {
    return LOCATION_PALETTE[index % LOCATION_PALETTE.length];
  }
  const k = String(key || "other");
  let h = 0;
  for (let i = 0; i < k.length; i += 1) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return LOCATION_PALETTE[h % LOCATION_PALETTE.length];
}

// Seed buttons are development conveniences only — never expose them in prod.
const SHOW_SEED_TOOLS = process.env.NODE_ENV !== "production";

// ── TV size preferences (⚙ next to Exit TV) ──
// Three independent zooms, persisted per device: OR cards grid, font/content
// inside each card, and the side panels (MD Call / CRNA-AA / Broadcast).
// The in-card zoom rides a CSS var so RoomCard needs no prop plumbing; the
// rule only bites while body[data-tv-mode] is set, so it is TV-only.
const TV_SIZES_KEY = "atcTvSizes";
const TV_SIZE_DEFAULTS = { card: 1, font: 1, panel: 1, cols: 0 }; // cols 0 = auto
const loadTvSizes = () => {
  try { return { ...TV_SIZE_DEFAULTS, ...JSON.parse(localStorage.getItem(TV_SIZES_KEY) || "{}") }; }
  catch { return { ...TV_SIZE_DEFAULTS }; }
};
if (typeof document !== "undefined" && !document.getElementById("atc-tv-zoom-style")) {
  const el = document.createElement("style");
  el.id = "atc-tv-zoom-style";
  el.textContent = `body[data-tv-mode="1"] .atc-room-card { zoom: var(--tv-font-zoom, 1); }`;
  document.head.appendChild(el);
}
// Default supervision limit (D-08). Phase 5.4: the live value comes from
// facility.rules.supervisionRatio (resolveFacilityRules) — this constant is
// only the resolver's fallback documentation.
const SUPERVISOR_ROOM_LIMIT = 4;

export default function BoardPage() {
  const {
    user, realUser, isAIC, isAdmin, isDoctor, isProvider, isObserver, canChangeStatus,
    canManageAssignments, mustNotifyAIC, testingMode, isBoardManager,
  } = useAuth();
  const toast = useToast();
  const isMobile = useIsMobile();
  // Full-screen TV mode (Full Board view only) — hides all chrome. A Board
  // Manager (wall device) LANDS in TV mode: it's their default home screen.
  // `?tv=1` opens straight into TV mode (the Timeline page's "TV Mode" button
  // navigates here with it).
  const wantsTvParam = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tv") === "1";
  const [tvMode, setTvMode] = useState(isBoardManager || wantsTvParam);
  // TV-ONLY facility override: the wall display can show any facility WITHOUT
  // touching user.activeFacilityId (which live-syncs to every session the
  // account has open). Cleared automatically when TV mode exits.
  const [tvFacilityId, setTvFacilityId] = useState(null);
  useEffect(() => {
    if (!tvMode) setTvFacilityId(null);
  }, [tvMode]);
  // Campus leaders (WC AIC / Tampa DOD / Carrollwood AIC) for the MD Call
  // colour-coding. Cross-facility board reads are rules-gated by role.
  const facilityBoards = useAicByFacility(useDayKey(), {
    enabled: isAdmin || isDoctor || isAIC || isBoardManager,
  });
  // TV size preferences (⚙ modal) — persisted per device, applied TV-only.
  const [tvSizes, setTvSizesState] = useState(loadTvSizes);
  const [showTvSettings, setShowTvSettings] = useState(false);
  const setTvSize = (key, value) => {
    setTvSizesState((prev) => {
      const next = { ...prev, [key]: value };
      try { localStorage.setItem(TV_SIZES_KEY, JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  };
  useEffect(() => {
    const b = document.body;
    if (tvMode) b.style.setProperty("--tv-font-zoom", String(tvSizes.font));
    else { b.style.removeProperty("--tv-font-zoom"); setShowTvSettings(false); }
    return () => b.style.removeProperty("--tv-font-zoom");
  }, [tvMode, tvSizes.font]);
  const facilityId = (tvMode && tvFacilityId) || user?.activeFacilityId;
  // Cross-facility TV = a BASIC overview (case/people/time distribution only):
  // no break system, no alerts, no actions. Full-functional TV stays exclusive
  // to the facility you actually work in (tvFacilityId is null there).
  const tvRemoteView = tvMode && !!tvFacilityId;
  // LOCAL day key, reactive across midnight so a board left open overnight
  // re-subscribes to the new day's roster/assignments instead of freezing.
  const realDayKey = useDayKey();
  // Preview Tomorrow (read-only): every subscription below keys off dateKey,
  // so one swap shows tomorrow's staged board exactly as it will go live at
  // midnight — when the day rolls, tomorrow simply BECOMES today.
  const [previewTomorrow, setPreviewTomorrow] = useState(false);
  const dateKey = previewTomorrow ? nextDayKey(realDayKey) : realDayKey;
  // Live case phases for this board, so a card can name what the room's CRNA
  // last stamped on their phone. Read-only here — every write goes through
  // useCasePhases from the Room Status screen.
  const { openCaseFor } = useRoomCases(facilityId, dateKey);
  // ⚠️ THE SAME ORCHESTRATOR THE PHONE USES. A phase stamped on the board and
  // one stamped on the Room Status screen must leave the room in the same
  // state, so both go through useCasePhases and neither re-derives the
  // phase → board-status mapping.
  const { stampPhase } = useCasePhases();
  const { rooms, loading, error: roomsError } = useRooms(facilityId);
  const { roster } = useDailyRoster(facilityId, dateKey);
  const { locations, active: activeLocs } = useLocations(facilityId);
  // Live facility doc — the authoritative current facility name for the TV
  // header (works for every role; not the possibly-stale user profile field).
  const { facility } = useFacility(facilityId);
  // Canonical KNOWN_FACILITIES label first — facility docs (tampa/carrollwood)
  // have no `name` field and user.activeFacilityName can be stale or belong to
  // a different facility than a TV override.
  const knownFacilityLabel = facilityLabel(facilityId);
  const facilityName =
    knownFacilityLabel && knownFacilityLabel !== facilityId
      ? knownFacilityLabel
      : facility?.name || user?.activeFacilityName || "OR Board";
  // TV facility switcher options — same visibility rule as the NavMenu
  // switcher: admins every non-archived facility, others their active ones.
  const allFacilities = useFacilities();
  const tvFacilityOptions = allFacilities.filter((f) => {
    const status = resolveFacilityStatus(f);
    if (isAdmin) return status !== "archived";
    const mine = Array.isArray(user?.facilityIds) ? user.facilityIds : [];
    return status === "active" && mine.includes(f.id);
  });
  // Phase 5.4 — per-facility rules read at runtime (never hard-coded).
  const facilityRules = resolveFacilityRules(facility);
  // Break approval retired: a request goes straight to the board (yellow
  // card) and any free CRNA/doctor covers it — no queue, no doctor gate.
  const { emergencyRoomIds, urgencyForRoom } = useAlertCenter();
  // TV-mode broadcast announcements (live; AIC writes — Prompt 3).
  const broadcasts = useBroadcasts(facilityId, dateKey);
  // People pool = the Anesthesia Staff master list. The day's roster (below)
  // gates it to the assigned subset, which is what actually shows on the board.
  const { doctors, providers } = useStaff();
  // Board shows occupied rooms (the filter pills were removed). Kept as a const
  // so the existing filter logic still reads "all".
  const [filter] = useState("all");
  // Board location lives in the URL (?loc=key) so the NavMenu dropdown can set
  // it from any page; "all" when absent.
  const [searchParams] = useSearchParams();
  // ?loc= keys belong to the USER's facility — while a TV facility override is
  // active, show all of that facility's locations instead of a foreign key.
  const locFilter = tvMode && tvFacilityId ? "all" : searchParams.get("loc") || "all";
  const [currentTab, setCurrentTab] = useState("my");
  const [showAssign, setShowAssign]   = useState(false);
  // When the AIC clicks "Assign" on a bench (unassigned roster) provider, open
  // the assignment modal pre-selected to that CRNA/AA so they only pick room + MD.
  const [assignProviderId, setAssignProviderId] = useState(null);
  // Bench "Assign" now opens a 3-way chooser (New Room / Takeover / Home) before
  // the assignment modal. Holds the provider object being placed, or null.
  const [benchProvider, setBenchProvider] = useState(null);
  // Same chooser for an unassigned DOCTOR (New Room / Takeover / Home).
  const [benchDoctor, setBenchDoctor] = useState(null);
  // AIC-handoff interlock: { person, after } — a board flow tried to send the
  // CURRENT AIC home; `after()` runs only once a successor is named.
  const [aicHandoff, setAicHandoff] = useState(null);
  const [aicHandoffBusy, setAicHandoffBusy] = useState(false);
  // The New-Room path opens the assignment modal in simplified mode (room/times/
  // CRNA/MD only); doctorMode shows only Room + MD (preset to assignAttendingId).
  const [simplifiedAssign, setSimplifiedAssign] = useState(false);
  const [doctorAssignMode, setDoctorAssignMode] = useState(false);
  const [assignAttendingId, setAssignAttendingId] = useState(null);
  // "Add Provider" → add staff from another facility onto today's roster.
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const navigate = useNavigate();

  // TV mode "fit to one screen": the board is a fluid grid that fills the box.
  // (refs + state here; the measuring effect runs below, after `sections`.)
  const tvFitRef = useRef(null);   // available area (the usable TV box)
  const tvBoardRef = useRef(null); // the board (for the overflow safety scale)
  const tvPanelsRef = useRef(null); // side panels — measured for the fit math
  // Measured available area (the TV's usable box). Drives the smart column
  // count so the board's natural shape matches whatever screen it's on.
  const [tvAvail, setTvAvail] = useState({ w: 0, h: 0, roomsW: 0 });
  // Safety down-scale: 1 normally; <1 only when even the max columns can't fit
  // the board in the screen height (an undersized display) so it shrinks to fit
  // instead of overlapping. Width is filled by fluid columns, not by scaling.
  const [tvScale, setTvScale] = useState(1);
  // Manual master scale (Prompt 5): a TV slider multiplies the auto resolution
  // scale so the user can nudge everything bigger/smaller while staying
  // harmonious (all elements scale together via the --atc-scale CSS var).
  const [manualScale, setManualScale] = useState(1);
  const viewport = useViewport();

  // Signal TV mode to the app shell so the global Messages launcher (a fixed
  // FAB mounted in App.js) hides on the wall display. Body attribute + event so
  // the launcher can react without a shared context.
  // Prompt 5: publish the resolution-aware scale (× manual master) to :root so
  // room cards AND portaled modals scale harmoniously in TV mode. Reset to 1 on
  // exit/unmount so no other page inherits it.
  useEffect(() => {
    const root = document.documentElement;
    if (tvMode) {
      const s = tvScaleForViewport(viewport.width, viewport.height) * manualScale;
      root.style.setProperty("--atc-scale", String(s));
    } else {
      root.style.setProperty("--atc-scale", "1");
    }
    return () => root.style.setProperty("--atc-scale", "1");
  }, [tvMode, viewport.width, viewport.height, manualScale]);

  useEffect(() => {
    if (tvMode) document.body.setAttribute("data-tv-mode", "1");
    else document.body.removeAttribute("data-tv-mode");
    window.dispatchEvent(new Event("atc-tv-mode"));
    return () => {
      document.body.removeAttribute("data-tv-mode");
      window.dispatchEvent(new Event("atc-tv-mode"));
    };
  }, [tvMode]);

  // TV mode: best-effort real fullscreen; Escape or leaving fullscreen exits.
  // The board itself stays mounted (real RoomCards, all actions live) — TV mode
  // only hides the chrome via the {!tvMode && …} gates below.
  useEffect(() => {
    if (!tvMode) return undefined;
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    const onKey = (e) => { if (e.key === "Escape") setTvMode(false); };
    const onFs = () => { if (!document.fullscreenElement) setTvMode(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFs);
      if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
    };
  }, [tvMode]);

  // Today's assignment-sheet rows — each room card shows its matching row
  // (start, surgeon, CRNA + relief, MD/DO + relief).
  useEffect(() => {
    if (!facilityId) return undefined;
    const unsub = listenToAssignments(facilityId, dateKey, (snap) => {
      setAssignments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [facilityId, dateKey]);

  // Providers in transit to/from THIS facility (Traveling section).
  const [transfers, setTransfers] = useState([]);
  useEffect(() => {
    if (!facilityId) return undefined;
    const unsub = listenToTransfers(
      facilityId, dateKey,
      (snap) => setTransfers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("[BoardPage] transfers listener failed:", e)
    );
    return unsub;
  }, [facilityId, dateKey]);

  // Roles that share the My Rooms / Full Board tab pattern (D-05/D-06/D-07/D-09).
  // Observers get the Full Board only — no tabs, no My Rooms (D-10).
  const hasMyRooms = isAIC || isAdmin || isDoctor || isProvider;

  const runSeed = async (fn, label) => {
    try {
      await fn();
      toast.success(`${label} added.`);
    } catch (err) {
      console.error(`Seed (${label}) failed:`, err);
      toast.error(`Could not add ${label.toLowerCase()}.`);
    }
  };

  // One-time: rebind rooms that still carry legacy location codes (wor, baby,
  // endo, cl, rad) to the matching Facility-Settings location. Idempotent.
  // A room scheduled on today's assignment sheet shows on the board even
  // while its live status is still "empty" — the sheet creates the day.
  const isScheduled = (r) =>
    hasAssignmentData(assignmentForRoom(r.name, assignments));

  // Location switcher (AIC/Admin) — scope the board to one facility location.
  // "all" shows every location, grouped into sections as before. TBD rooms are
  // an internal placeholder — never shown on the board.
  const shownRooms = rooms.filter(r => !isTbdRoom(r));
  const locRooms = locFilter === "all"
    ? shownRooms
    : shownRooms.filter(r => canonicalKey(r.location) === locFilter);

  // A room is "occupied" if its live status is non-empty OR it's scheduled on
  // today's sheet. Single source of truth so the badge filter, the stat count,
  // and the Empty bucket all agree (B2 fix — a scheduled room is never shown as
  // both "Occupied Rooms" and "Empty").
  const isOccupied = (r) => normalizeStatus(r.status) !== "empty" || isScheduled(r);

  // Hide empty rooms by default — "Occupied Rooms" shows active or scheduled
  const filtered = locRooms.filter(r => {
    const st = normalizeStatus(r.status);
    if (filter === "empty") return !isOccupied(r);
    if (filter === "all")   return isOccupied(r);
    if (filter === "break") return r.onBreak;
    return st === filter;
  });

  // "My Rooms" — rooms this user supervises or staffs (D-06/D-07), plus rooms
  // where today's assignment sheet schedules them (before sign-in). Resolved the
  // SAME way as the My Room page: assignments/rooms hold STAFF ids + names (which
  // may carry a "Dr." the users-doc strips), so match via myRoomAssignments /
  // userStaffIds (id, honorific-insensitive name, OR short code) — NOT a raw
  // user.uid compare, which silently matched nothing.
  const staffForMatch = [...doctors, ...providers];
  const myStaffIds = userStaffIds(user, staffForMatch);
  const myAssignments = myRoomAssignments(assignments, user, staffForMatch);
  const myRooms = locRooms.filter(r => {
    const signedIn =
      normalizeStatus(r.status) !== "empty" &&
      (myStaffIds.has(r.supervisingDoctorId) || myStaffIds.has(r.providerId));
    return signedIn || !!assignmentForRoom(r.name, myAssignments);
  });

  // EMERGENCY in My Rooms (spec R3): EVERY supervisor (doctor / AIC / admin) sees
  // an emergency room blinking in their My Rooms — even one they don't cover — so
  // any doctor can step in. Providers do NOT get this: they see an emergency only
  // on the Full Board (they must navigate there), never pushed into My Rooms.
  const myRoomsBase = (isDoctor || isAIC || isAdmin)
    ? [...myRooms, ...locRooms.filter(r => emergencyRoomIds.has(r.id) && !myRooms.some(m => m.id === r.id))]
    : myRooms;
  // ⚡ URGENCY in My Rooms: a room with a live urgency that targets ME (its
  // supervising doctor or its CRNA) is pushed into My Rooms even when I'm not
  // currently signed into it (e.g. the room was cleared/emptied after the
  // ping) — otherwise the amber bar would render nowhere and the ping would be
  // unclearable. urgencyForRoom is viewer-scoped, so this never adds a room
  // for anyone the ping doesn't concern.
  const myRoomsView = [
    ...myRoomsBase,
    ...locRooms.filter(r => urgencyForRoom(r.id) && !myRoomsBase.some(m => m.id === r.id)),
  ];

  // Which rooms to show given the active tab. Observers always see the full
  // read-only board (D-10). TV mode is ALWAYS the full board — landing in TV
  // directly (?tv=1 from the Timeline page, or a Board Manager device) used
  // to inherit the default "My Rooms" tab and render 0 rooms (user photo).
  const showMyTab = !tvMode && hasMyRooms && currentTab === "my";
  const visibleRooms = showMyTab ? myRoomsView : filtered;

  // Read-only rendering (D-09/D-10): the provider's Full Board tab and the whole
  // observer board hide every action affordance. Edit/manage flow from the
  // permission flags otherwise (Phase 3 owns real enforcement).
  const readOnlyView = isObserver || (isProvider && !showMyTab) || previewTomorrow;
  const cardCanEdit = readOnlyView ? false : canChangeStatus;
  const cardCanManage = readOnlyView ? false : canManageAssignments;
  // Bench sections (Unassigned/Home/Traveling) render in TV mode too, but
  // their buttons stay hidden on a cross-facility (basic) TV view.
  const benchCanManage = cardCanManage && !tvRemoteView;

  // Board is organized into ordered location sections, each a vertical column.
  const sections = groupRoomsByLocation(visibleRooms, locations);

  // TV "smart fill": choose the FEWEST room-grid columns (= biggest cards) whose
  // stacked rows still fit the measured screen HEIGHT. Fewer columns = larger,
  // more legible cards; but if that makes the board taller than the screen the
  // cards would have to overlap, so we add columns until the rows fit. Width is
  // then filled by fluid `fr` columns and height by section grow (below), so the
  // board fills the whole TV with no side or bottom band and no overlap.
  // Rows are summed PER location section (each rounds up + has a header) to match
  // the real stacked layout. CARD_MIN_H is a room card's natural content height.
  // TV renders ALL rooms in one flat uniform grid (location is shown on each
  // card), so a one-room location never wastes a whole full-width row. Rooms are
  // kept in location order for a familiar grouping.
  const tvFlatRooms = sections.flatMap((sec) => sec.rooms);
  // Per-room location tint for TV mode — each card sits on its location's colour
  // (the SAME LOCATION_PALETTE the board sections use) so a wall viewer can tell
  // locations apart in the one flat TV grid. Keyed by room id from the ordered
  // sections so the tint matches the board exactly.
  const tvTintByRoom = new Map();
  sections.forEach((sec, idx) => {
    // groupRoomsByLocation returns { key, label, rooms } — `.key`, not `.id`
    // (a `.id` read here returned undefined and broke React list keys).
    const t = locationTint(sec.key, idx);
    sec.rooms.forEach((r) => tvTintByRoom.set(r.id, t));
  });
  // Responsive TV fit (domain/tvLayout): pick the column count that gives the
  // LARGEST uniform cards for the DETECTED screen — width AND height aware,
  // resolution-scale aware (--atc-scale: 1080p=1, 4K=2), bench-aware. The
  // measured shrink transform below stays as the safety net, so the final
  // render always fits exactly with every element scaled proportionally.
  // Rough pre-render bench estimate (the exact flags derive further down):
  // travelers, departed-at-this-facility, or more rostered CRNAs than staffed
  // rows all imply a bench section under the grid.
  const hasTvBenchesEstimate =
    transfers.length > 0 ||
    Object.values(roster?.departed || {}).some(Boolean) ||
    (Array.isArray(roster?.crnas) ? roster.crnas.length : 0) >
      assignments.filter((a) => a.ahpId).length;
  let tvCols = 4;
  if (tvMode && tvAvail.h > 0 && tvFlatRooms.length > 0) {
    const resScale = tvScaleForViewport(viewport.width, viewport.height) * manualScale;
    tvCols = pickTvColumns({
      availW: tvAvail.roomsW || Math.max(1, tvAvail.w - 300),
      availH: tvAvail.h,
      roomCount: tvFlatRooms.length,
      scale: resScale * (tvSizes.card || 1),
      benchH: hasTvBenchesEstimate ? 190 * resScale : 0,
    }).cols;
  }
  // ⚙ Sizes override: an explicit 1–5 column choice beats the auto fit
  // (0 = Auto keeps the algorithm's pick).
  if (tvSizes.cols >= 1) tvCols = Math.min(5, Math.max(1, Math.round(tvSizes.cols)));

  // TV-mode info panels (Prompt 2): MD Call roster (names + phones, call order)
  // and CRNA roster grouped by shift. Derived from today's roster + staff pool.
  const tvMdCall = buildMdCallList(roster, staffForMatch);
  // Sheet-imported facility-wide MD call list (same source the Daily Assignments
  // MdCallPanel uses): resolve each short code → full name via the staff pool so
  // the TV shows the rich PC→num→L badges (WC red / other navy) identically.
  const tvSheetMdCall = Array.isArray(roster.mdCallList) ? roster.mdCallList : [];
  // The LIVE AIC's short code — the sheet's isAIC flag is import-time state;
  // after an AIC handoff the badge must follow roster.aicId (user report:
  // "not changing the AIC" — the handoff wrote aicId but the panel badge
  // still pointed at the old doctor).
  const liveAicCode = String(
    staffForMatch.find((d) => d.id === roster.aicId || d.uid === roster.aicId)?.shortCode || ""
  ).toUpperCase();
  const tvMdCallList = tvSheetMdCall.map((e) => {
    const match = staffForMatch.find((d) => String(d.shortCode || "").toUpperCase() === e.code);
    return {
      ...e,
      name: match?.name || e.code,
      // Badge follows the CURRENT AIC; fall back to the sheet flag only when
      // the roster has no AIC (pre-handoff boards keep working).
      isAIC: liveAicCode ? e.code === liveAicCode : Boolean(e.isAIC),
      // Locums sort to the bottom of the MD call list (P4). Flag from the staff
      // profile's employmentType, unless the sheet already marked it.
      isLocum: Boolean(e.isLocum || match?.employmentType === "locums"),
    };
  });
  // Doctors removed from MD Call and pending Home confirmation (X button).
  const mdCallHome = Array.isArray(roster.mdCallHome) ? roster.mdCallHome : [];
  // Remove a doctor from today's MD call list (they left). The call list is
  // facility-WIDE, so the removal syncs to Wesley Chapel + Carrollwood +
  // Tampa boards at once (best-effort per campus); the doctor lands in the
  // Home box with Back / Confirm Home.
  const removeMdCall = async (code) => {
    const entry = tvMdCallList.find((e) => e.code === code) || { code };
    try {
      await syncMdCallRemoval(dateKey, { code, name: entry.name || code }, user?.uid);
      toast.success(`${entry.name || code} removed from MD Call on all boards — confirm Home below.`);
    } catch (err) {
      console.error("Remove MD call failed:", err);
      toast.error("Couldn't remove from the call list. Try again.");
    }
  };
  // Back / Cancel — restore the doctor to MD Call on every board.
  const restoreMdCall = async (entry) => {
    try {
      await syncMdCallRestore(dateKey, entry.code, user?.uid);
      toast.success(`${entry.name || entry.code} is back on MD Call.`);
    } catch (err) {
      console.error("Restore MD call failed:", err);
      toast.error("Couldn't restore the doctor to MD Call.");
    }
  };
  // Confirm Home — the doctor is gone for the day: clear the pending entry on
  // every board and drop them from every roster list (they leave the CRNA/
  // supervisor boxes too).
  const confirmMdCallHome = async (entry) => {
    const staffId = resolveMdCallStaff(entry.code, staffForMatch)?.id || null;
    const after = async () => {
      await syncMdCallConfirmHome(dateKey, entry.code, staffId, user?.uid);
      toast.success(`${entry.name || entry.code} confirmed home.`);
    };
    // Sending the current AIC home first demands a successor (handoff modal).
    if (staffId && guardAicHome({ id: staffId, name: entry.name || entry.code }, after)) return;
    try {
      await after();
    } catch (err) {
      console.error("Confirm MD home failed:", err);
      toast.error("Couldn't confirm home.");
    }
  };
  // The pending MD-call-removal entry for a doctor (staff id via shortCode, or
  // honorific-insensitive name). Drives the Home-box row, the bench EXCLUSION
  // (an X-removed doctor lives in the Home box ONLY — user spec), and the
  // "Back to MD Call" choice if they ever surface in an Assign chooser.
  const mdCallPendingFor = (doc) => {
    if (!doc) return null;
    const nm = String(doc.name || "").replace(/^dr\.?\s+/i, "").trim().toLowerCase();
    return (
      mdCallHome.find((e) => {
        const st = resolveMdCallStaff(e.code, staffForMatch);
        if (st && (st.id === doc.id || st.uid === doc.id)) return true;
        const en = String(e.name || st?.name || "").replace(/^dr\.?\s+/i, "").trim().toLowerCase();
        return Boolean(en && nm && en === nm);
      }) || null
    );
  };
  const pendingHomeStaffIds = new Set(
    mdCallHome.map((e) => resolveMdCallStaff(e.code, staffForMatch)?.id).filter(Boolean)
  );
  const tvCrnaGroups = buildCrnaShiftGroups(roster, assignments);

  // TV-mode fit-to-screen: measure the board's natural size vs the available area
  // and scale the grid (transform) so everything fits one screen with no scroll.
  // scrollWidth/Height are layout sizes (transform-independent) so recompute is
  // stable. Runs on TV toggle, viewport resize, and room/section changes.
  useEffect(() => {
    if (!tvMode) { setTvScale(1); return undefined; }
    // Measure the usable box (drives the column heuristic) and, as a safety,
    // down-scale the board only if its natural height still overflows the screen
    // at the chosen columns — so an undersized display shrinks to fit rather than
    // overlapping. Width is filled by fluid columns, so we never scale for width.
    const measure = () => {
      const outer = tvFitRef.current, inner = tvBoardRef.current;
      if (!outer) return;
      const availW = outer.clientWidth, availH = outer.clientHeight;
      if (!availW || !availH) return;
      // MEASURED rooms-area width (screen minus the real side panels) — the
      // fit algorithm must never guess the panel width.
      const panelsW = tvPanelsRef.current ? tvPanelsRef.current.offsetWidth + 14 : 0;
      const roomsW = Math.max(1, availW - panelsW);
      setTvAvail((prev) => (prev.w === availW && prev.h === availH && prev.roomsW === roomsW ? prev : { w: availW, h: availH, roomsW }));
      if (inner) {
        const natH = inner.scrollHeight;
        const k = natH > availH ? availH / natH : 1;
        setTvScale((prev) => (Math.abs(prev - k) < 0.01 ? prev : (k > 0 && Number.isFinite(k) ? k : 1)));
      }
    };
    const raf = requestAnimationFrame(measure);
    let ro;
    if (typeof ResizeObserver === "function") {
      ro = new ResizeObserver(measure);
      if (tvFitRef.current) ro.observe(tvFitRef.current);
      if (tvBoardRef.current) ro.observe(tvBoardRef.current);
    }
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [tvMode, sections.length, visibleRooms.length, tvCols]);
  // (Bench sections appearing/disappearing change the column height too — the
  // ResizeObserver on tvBoardRef re-measures without extra deps.)

  // Overload flag (D-08): a plain supervising doctor (not AIC/Admin) covering
  // more than SUPERVISOR_ROOM_LIMIT rooms gets the 5th-onward rooms flagged,
  // but only on their My Rooms tab. The flagged rooms are the 5th+ in the
  // board's deterministic order (flattened sections), so everyone sees the same
  // flags. AIC/Admin hold full-board control and are never overload-flagged.
  // Emergency popup eligibility: the AIC/admin watch the whole board; a plain
  // supervising doctor gets the popup only when actively covering MORE THAN ONE
  // room. A doctor in a single case room is not interrupted by a center popup.
  const monitorsEmergencyPopup =
    isAIC || isAdmin || countRoomsSupervisedBy(rooms, assignments, myStaffIds) > 1;

  const isPlainSupervisor = isDoctor && !isAIC && !isAdmin;
  const overloadedRoomIds = new Set();
  if (isPlainSupervisor && showMyTab) {
    const orderedSupervised = sections
      .flatMap(sec => sec.rooms)
      .filter(r => myStaffIds.has(r.supervisingDoctorId));
    if (orderedSupervised.length > (facilityRules?.supervisionRatio || SUPERVISOR_ROOM_LIMIT)) {
      orderedSupervised
        .slice(facilityRules?.supervisionRatio || SUPERVISOR_ROOM_LIMIT)
        .forEach(r => overloadedRoomIds.add(r.id));
    }
  }

  // Persist a supervising doctor's day call number from the Add Assignment
  // modal (B4). Upserts the member onto today's roster — same store the Daily
  // grid's call-order sub-select writes, so MD Call stays consistent.
  const handleSetCallOrder = async (attendingId, callOrder) => {
    const list = roster.supervisors || [];
    const next = list.some((m) => m.id === attendingId)
      ? list.map((m) => (m.id === attendingId ? { ...m, callOrder } : m))
      : [...list, { id: attendingId, name: doctorPool.find((d) => d.id === attendingId)?.name || "", callOrder }];
    await setDailyRoster(facilityId || "default", dateKey, { supervisors: next }, user?.uid);
  };

  const today = new Date().toLocaleDateString("en-US", {
    weekday:"short", month:"short", day:"numeric"
  });

  // Staff pools — testing mode injects the code-defined testing accounts.
  const doctorPool   = testingMode ? [...doctors, ...testDoctors()] : doctors;
  const rawProviderPool = testingMode ? [...providers, ...testProviders()] : providers;
  // The CRNA's per-day shift (leave time) lives on the daily roster's crnas
  // list, NOT the staff profile. Merge it over the pool so the leave time is
  // correct and TRAVELS when a CRNA is reassigned via the Add Assignment modal
  // or Takeover (both read this pool). Mirrors DailyAssignmentPage's day-field
  // merge; RoomCard then resolves "Leaving HH:MM" from this enriched pool.
  const crnaDayById = new Map((roster.crnas || []).map((c) => [c.id, c]));
  const providerPool = rawProviderPool.map((p) => {
    const day = crnaDayById.get(p.id);
    if (!day) return p;
    return {
      ...p,
      ...(day.shiftCode != null ? { shiftCode: day.shiftCode } : {}),
      ...(day.goHomeHour != null ? { goHomeHour: day.goHomeHour } : {}),
    };
  });

  // Only people on today's Daily Provider Assignment List may be assigned.
  const allowedDoctorIds   = new Set([roster.aicId, ...roster.supervisors.map(srv => srv.id)].filter(Boolean));
  const allowedProviderIds = new Set(roster.crnas.map(c => c.id));
  // A CRNA counts as assigned if they hold a live room (room.providerId) OR are
  // named on a room's assignment row (assignment.ahpId). The sheet/PDF importer
  // writes assignment docs, not room.providerId, so checking rooms alone left
  // every imported CRNA looking "unassigned" on the bench.
  // ⚠️ A STANDING OB WATCH DOES NOT MAKE SOMEBODY ASSIGNED.
  // The on-call CRNA covers epidurals and sections; between them they are FREE,
  // and the whole point of the rule is that the board can send them. Their
  // on-call row carries their ahpId, so without this filter they sat in no list
  // at all — invisible on the bench while visibly idle on the chart.
  //
  // `isStandingOnCall` is imported, not re-derived: the sheet writes 07:00→07:00
  // rather than blank times, and a second copy of that predicate here would
  // drift from the chart's the first time either changed.
  // A MAP, NOT A SET, because the chip has to name the service. Two standing
  // watches on one board is ordinary (WC OB and WC IR, 2026-08-20), and a set of
  // ids could only ever say "on call for something".
  const standingWatchRoomByAhpId = new Map(
    assignments.filter(isStandingOnCall).filter((a) => a.ahpId).map((a) => [a.ahpId, a.orRoom || ""])
  );
  const standingWatchAhpIds = standingWatchRoomByAhpId;
  // Providers a row still claims AT THIS MOMENT — the clocked version of
  // `assignedProviderIds`, for decisions about who is still in a room rather
  // than who appears anywhere on today's sheet.
  const claimingProviderIds = new Set(
    assignments
      .filter((a) => rowClaimsAt(a, nowMinutes()))
      .map((a) => a && a.ahpId)
      .filter(Boolean)
  );
  const assignedProviderIds = new Set(
    [...rooms.map(r => r.providerId), ...assignments.map(a => a.ahpId)]
      .filter(Boolean)
      .filter((id) => !standingWatchAhpIds.has(id))
  );
  // A doctor counts as "assigned" if they supervise any room (live or sheet) OR
  // are mid-takeover (staged on a room, blinking) — so they leave the Unassigned
  // box the moment the takeover starts.
  const assignedDoctorIds = new Set(
    [
      ...rooms.map(r => r.supervisingDoctorId),
      ...assignments.map(a => a.attendingId),
      ...assignments.map(a => a.mdTakeoverId),
    ].filter(Boolean)
  );
  const rosterDoctors   = doctorPool.filter(d => allowedDoctorIds.has(d.id));
  // Doctors enriched with today's per-day call order (roster.supervisors) so the
  // room cards can show the MD call number next to the supervising doctor.
  const callOrderById = new Map((roster.supervisors || []).map(s => [s.id, s.callOrder]));
  const doctorsWithCall = doctorPool.map(d =>
    callOrderById.has(d.id) ? { ...d, callOrder: callOrderById.get(d.id) } : d
  );

  // Roster-driven member objects so the Unassigned/Home boxes show EVERYONE on
  // today's roster — including cross-facility / non-pool people who aren't in the
  // local staff pool. Enrich from the pool when present; the roster entry's own
  // fields (name, day shift/call) win.
  const departedMap = roster.departed || {};
  // GIVING BREAKS is a LABEL, never a filter: the person doing the break rounds
  // stays in Unassigned, because they are still here and still callable. This
  // board derives its own bench (it predates `benchProviders`), so it reads the
  // same roster map rather than a second definition of the fact.
  const givingBreaksMap = roster.givingBreaks || {};
  const dedupeById = (arr) => Array.from(new Map(arr.filter(m => m && m.id).map(m => [m.id, m])).values());
  const crnaMembers = dedupeById(roster.crnas || []).map(m => ({ ...(providerPool.find(p => p.id === m.id) || {}), ...m }));
  const doctorMembers = dedupeById([
    ...(roster.aicId ? [{ id: roster.aicId, name: roster.aicName }] : []),
    ...(roster.supervisors || []),
  ]).map(m => ({ ...(doctorPool.find(p => p.id === m.id) || {}), ...m }));

  // Unassigned bench = rostered CRNAs not in a room AND not sent home.
  // Anyone actively traveling between facilities lives in the Traveling
  // section ONLY — never a bench and never Home, on EITHER board, until
  // Arrival or Cancel resolves the trip. (A person can legitimately sit on
  // both facilities' day rosters after a round trip; this exclusion is what
  // keeps them from showing up "already here" mid-transit.)
  const travelingIds = new Set(transfers.map((t) => t.providerId));
  // The flag travels ON the bench entry, because BenchAssignFlow's button is a
  // TOGGLE and reads it to decide whether it says "Give Breaks" or "Stop Giving
  // Breaks". Handing that modal a plain roster member would leave it permanently
  // offering to set a label that is already set.
  const rosterProviders = crnaMembers
    .filter(m => !assignedProviderIds.has(m.id) && !departedMap[m.id] && !travelingIds.has(m.id))
    .map(m => (givingBreaksMap[m.id] ? { ...m, givingBreaks: true } : m));
  // Unassigned doctors = rostered doctors supervising no room AND not sent
  // home AND not X-removed from MD Call (those live in the Home box ONLY,
  // with Back / Confirm home — user spec 2026-07-08).
  const unassignedDoctors = doctorMembers.filter(m => !assignedDoctorIds.has(m.id) && !departedMap[m.id] && !travelingIds.has(m.id) && !mdCallPendingFor(m));
  // Home box = rostered people (CRNAs + doctors) marked departed for the day.
  const homeProviders = [...crnaMembers, ...doctorMembers].filter(
    m => !!departedMap[m.id] && !travelingIds.has(m.id)
  );
  // Modal pool — roster-gated like the grid, but keep already-assigned CRNAs so
  // editing a room still shows its current pick instead of a blank dropdown.
  // ⚠️ DEPARTED IS AN ORTHOGONAL AXIS, AND SKIPPING IT HERE REACHED THE RELIEVER
  // LISTS. This pool is deliberately NOT the bench — an already-assigned CRNA
  // must stay in it so editing a room shows its current pick instead of a blank
  // dropdown. The exclusion that got skipped along with "assigned" was
  // `departed`, so every reliever and break-cover list still contained people
  // who had left the building. `domain/departure.js:11` states the rule the
  // board was breaking verbatim: "A departed person disappears from every
  // dropdown and reliever list (D-06)."
  //
  // Why it is worse than an empty list: naming a departed CRNA as cover writes
  // `relieverId`, the room card then reads "On Break — covered by …", and every
  // other free provider is told that room is handled. Nobody comes, and the
  // person who asked for the break is still in the room with a patient. A room
  // that openly has no cover gets one; a room that falsely claims cover does not.
  //
  // Kept by exception: somebody already assigned to a room today, so an
  // existing pick still renders rather than vanishing mid-edit.
  // ⚠️ THE "STILL ASSIGNED" EXCEPTION IS BOUNDED BY THE CLOCK. It exists so an
  // in-progress edit still shows the room's current pick — but
  // `assignedProviderIds` is built from every row on the day with no clock, so
  // a CRNA whose only room finished at 11:00 and who went home at 15:00 stayed
  // in the relief dropdown all evening. `claimingProviderIds` counts only rows
  // that still claim somebody right now.
  const rosterProvidersAll = providerPool.filter(
    p => allowedProviderIds.has(p.id) && (!departedMap[p.id] || claimingProviderIds.has(p.id))
  );
  const rosterEmpty     = allowedDoctorIds.size === 0 && allowedProviderIds.size === 0;

  // People from today's Daily List who can cover a break: ALL rostered doctors
  // AND all rostered CRNAs/AAs (not just the unassigned bench) — a CRNA already
  // in a room can still give a colleague a break. Labelled by role.
  // ⚠️ NOBODY WHO HAS GONE HOME CAN GIVE A BREAK. Unlike the pool above there is
  // no "current pick" to preserve here — this list only ever answers "who could
  // come and cover", so it filters outright rather than by exception.
  const breakGivers = [
    ...filterActiveStaff(rosterDoctors, departedMap)
      .map(d => ({ id: d.id, name: d.name, kind: "Doctor" })),
    ...filterActiveStaff(rosterProvidersAll, departedMap)
      .map(p => ({ id: p.id, name: p.name, kind: (p.subRole || "crna").toUpperCase() })),
  ];

  // Any bench section on the TV board? Switches the TV grid to natural row
  // heights so the fit-to-screen scale can account for the sections below.
  const hasTvBenches = tvMode && !showMyTab && (
    rosterProviders.length + unassignedDoctors.length > 0 ||
    homeProviders.length > 0 ||
    mdCallHome.length > 0 ||
    transfers.length > 0
  );

  const columnStyle = isMobile
    ? { display: "grid", gridTemplateColumns: "1fr", gap: space.md }
    : s.column;
  // Room area inside a location panel: flex-wrap of fixed-width cards so the
  // panel sizes to its content. A small location (2 rooms) stays narrow and
  // packs beside the next location; a big one (Main OR) wraps to fill the row.
  const roomAreaStyle = isMobile
    ? { display: "grid", gridTemplateColumns: "1fr", gap: space.md }
    : tvMode
    // TV: a fluid grid of `tvCols` equal columns that always fills the section's
    // width (fr units), with natural card heights so nothing overlaps. The
    // column count is chosen from the screen height (below) so the rows fit.
    // TV: a fluid grid that fills BOTH dimensions. Equal `1fr` rows
    // (gridAutoRows) stretch to fill the screen height so the cards grow to use
    // all the space — no empty band — and the board adapts to any monitor size.
    // When bench sections (Unassigned/Home/Traveling) are on the TV board, the
    // rows switch to NATURAL height instead: stretched 1fr rows compress below
    // the cards' min height and the cards spill over the sections beneath them.
    // Natural heights make scrollHeight honest, so the fit-to-screen scale
    // shrinks the whole column (rooms + benches) onto one screen.
    ? { display: "grid", gridTemplateColumns: `repeat(${tvCols}, 1fr)`, gridAutoRows: hasTvBenches ? "auto" : "1fr", gap: 12, flex: hasTvBenches ? "0 0 auto" : 1, minHeight: 0, alignContent: hasTvBenches ? "start" : "stretch" }
    : { display: "flex", flexWrap: "wrap", gap: 10, alignContent: "flex-start" };
  // TV cards fill their grid cell width; height stays natural (no overlap).
  const cardWrapStyle = isMobile
    ? { width: "100%" }
    : tvMode
    ? { width: "100%", minWidth: 0, height: hasTvBenches ? "auto" : "100%", display: "flex", boxSizing: "border-box" }
    : { width: 248, minWidth: 0, display: "flex", boxSizing: "border-box" };

  // One place that builds a room card, reused by the per-location sections (board)
  // and the flat TV grid so their props never drift.
  // OB-coverage acknowledge: the AIC / a supervising doctor confirms they've seen
  // that the OB on-call CRNA left to cover another room. Stored on the on-call
  // assignment, keyed to the current absence so a new move re-alerts.
  const ackCoverage = async (asg, awayKey) => {
    if (!asg?.id || !awayKey) return;
    try {
      await updateAssignmentRow(asg.id, { coverageAckKey: awayKey, coverageAckByName: user?.name || "" });
    } catch (e) {
      console.error("[BoardPage] coverage ack failed:", e);
    }
  };

  // MD-call removal fallout for the room cards: the reassign picker offers
  // only the MD Call doctors still available (never the removed one).
  // ⚠️ DEPARTED, NOT JUST PENDING-HOME. This filtered only the MD-Call
  // "pending home" set, so a doctor actually sent home was still selectable as
  // a room's NEW supervising MD — picking them writes `attendingId` to somebody
  // who is not in the building, and the card then reads as supervised.
  // `domain/departure.js:11` states the rule: a departed person disappears from
  // every dropdown and reliever list.
  const availableRosterDoctors = rosterDoctors.filter(
    (d) => !pendingHomeStaffIds.has(d.id) && !departedMap[d.id]
  );

  // ── AIC-handoff interlock ──
  // Any board flow that sends a doctor HOME calls this. Sending the CURRENT
  // AIC home first demands a successor (dropdown of available doctors); the
  // aicId write happens BEFORE the departure so the day never runs without an
  // AIC. The successor inherits everything automatically — AuthContext derives
  // the active AIC from daily_boards.aicId, including the All-AICs chat.
  const guardAicHome = (person, after) => {
    if (person?.id && roster.aicId && person.id === roster.aicId) {
      setAicHandoff({ person, after });
      return true; // intercepted — caller must NOT proceed
    }
    return false;
  };
  const confirmAicHandoff = async (successor) => {
    if (!aicHandoff) return;
    setAicHandoffBusy(true);
    try {
      await setDailyRoster(facilityId, dateKey, { aicId: successor.id, aicName: successor.name || "" }, user?.uid);
      await aicHandoff.after();
      toast.success(`${successor.name} is now the AIC; ${aicHandoff.person.name} sent home.`);
      setAicHandoff(null);
    } catch (e) {
      console.error("AIC handoff failed:", e);
      toast.error("Could not hand off the AIC. Nothing was changed.");
    } finally {
      setAicHandoffBusy(false);
    }
  };

  // Who the VIEWER is in a given room — resolved from the row that is claiming
  // it right now, by staff id and never by uid.
  const roomMode = (room) =>
    caseWriterMode({
      uid: user?.uid || null,
      staffIds: myStaffIds,
      assignment: occupantRowForRoom(room.name, assignments, nowMinutes()),
      room,
    });

  const renderRoomCard = (room) => {
    // The row this room is RUNNING, not the first one Firestore returned. A
    // room with a morning and an afternoon case named an arbitrary one of them
    // all day; every control on the card (break, relief, next room, alerts)
    // hangs off this row.
    const asg = occupantRowForRoom(room.name, assignments, nowMinutes());
    const coverage = coverageStatus(asg, assignments, room);
    return (
      <RoomCard room={room} canEdit={!tvRemoteView && cardCanEdit} canManage={!tvRemoteView && cardCanManage} basic={tvRemoteView} locations={locations} breakGivers={breakGivers} overloaded={overloadedRoomIds.has(room.id)}
        providerGoHomeHour={providerPool.find(p => p.id === room.providerId)?.goHomeHour ?? null}
        assignment={asg}
        orCase={openCaseFor(room.id)}
        // Who the VIEWER is in THIS room — resolved from the live assignment by
        // staff id, never by uid (the two are different id spaces, which is what
        // made Room Status unreachable for everyone until 2026-09-07).
        canStampPhases={canStampCasePhases({ mode: roomMode(room), isAdmin, isAIC })}
        // Whether this viewer is IN this room, which is a narrower question than
        // whether they may stamp — see boardStatusActions.
        isRoomTeam={isRoomTeamMode(roomMode(room))}
        myStaffIds={myStaffIds}
        onStampPhase={(phaseId) =>
          stampPhase({
            orCase: openCaseFor(room.id),
            phaseId,
            room,
            uid: user?.uid || null,
            userName: user?.name || "",
            // Names the room's people if this stamp has to OPEN the case.
            // ⚠️ THE ROW CLAIMING THE ROOM NOW, not the first one Firestore
            // returned. This names `providerId` and `attendingId` on a case
            // document that becomes the permanent record — on a room with a
            // morning and an afternoon case, the arbitrary row put the MORNING
            // CRNA on the afternoon case.
            assignment: occupantRowForRoom(room.name, assignments, nowMinutes()),
            // STAFF ID IN, AUTH UID OUT. `attendingId` names a staff record;
            // push tokens live on `users/{uid}`. A staff id here writes a clean
            // directive that no phone ever rings for.
            supervisorUids: (() => {
              // Same reason: the afternoon phase must page the afternoon
              // attending, not whoever happened to be first in the array.
              const att = currentAttendingId(
                occupantRowForRoom(room.name, assignments, nowMinutes()),
                room
              );
              const doc = att ? (doctors || []).find((d) => d && d.id === att) : null;
              return doc?.uid ? [doc.uid] : [];
            })(),
            facilityId,
            dateKey,
          })
        }
        coverage={coverage}
        onAckCoverage={(awayKey) => ackCoverage(asg, awayKey)}
        // Alerts are TODAY-state: the read-only Tomorrow preview never wears
        // today's emergency/urgency/break-request visuals (suppressAlerts).
        emergency={!previewTomorrow && emergencyRoomIds.has(room.id)}
        suppressAlerts={previewTomorrow}
        // Urgency is private AND surface-scoped: only the My Rooms tab shows
        // it (plus the provider's own My Room page) — never the Full Board or
        // TV. urgencyForRoom already limits it to the targeted viewer (the
        // room's doctor or its CRNA); the alarm chirp/vibration in
        // AlertContext summons them even if they're on another page.
        urgency={showMyTab ? urgencyForRoom(room.id) : null}
        doctors={doctorsWithCall}
        rosterCrnas={rosterProvidersAll}
        rosterDoctors={availableRosterDoctors}
        // Supervisor pulled off MD Call (pending Home): the card shows
        // "Supervisor not available" with Reassign / Cancel. Today-state, so
        // never on the Tomorrow preview.
        removedSupervisor={previewTomorrow ? null : removedSupervisorForRoom(room, asg, mdCallHome, staffForMatch)}
        onCancelRemoved={restoreMdCall}
        rooms={rooms}
        allAssignments={assignments}
        fillHeight={tvMode} />
    );
  };

  // Shared bench-sections JSX (Unassigned / Home / Traveling) — placed in
  // TWO spots: in-flow after the location sections (board) or as grid items
  // after the last room card (TV), so it always follows the rooms.
  const benchSectionsJsx = (
    <>
              
              {/* Unassigned — Full Board AND TV mode (bottom of the board).
                  Rostered people not yet in a room. */}
              {!showMyTab && (rosterProviders.length + unassignedDoctors.length) > 0 && (() => {
                const tint = locationTint("__bench__", sections.length);
                return (
                  <section style={{ ...s.section, alignSelf: "flex-start", background: tint.bg, border: `1px solid ${tint.border}` }} aria-label="Unassigned staff">
                    <div style={{ ...s.sectionHead, background: tint.bg, borderBottom: `1px solid ${tint.border}` }}>
                      <h2 style={{ ...s.sectionTitle, color: tint.head }}>Unassigned</h2>
                      <span style={{ ...s.sectionCount, color: tint.head, borderColor: tint.border }}>{rosterProviders.length + unassignedDoctors.length}</span>
                    </div>
                    <div style={s.benchList}>
                      {unassignedDoctors.map(d => (
                        <div key={d.id} style={s.benchRow}>
                          <span style={s.benchName}>
                            {d.name}
                            <span style={s.benchRole}>
                              {" "}· Doctor
                              {/* NOTES-row locum coverage (Day Hawk / Night
                                  Float) — show the window + leaving time. */}
                              {d.coverKind ? ` · ${d.coverKind} til ${d.coverEndNextDay ? "7 AM" : "11 PM"}` : ""}
                            </span>
                          </span>
                          {benchCanManage && (
                            <button style={s.benchAssignBtn} onClick={() => setBenchDoctor(d)}>
                              Assign →
                            </button>
                          )}
                        </div>
                      ))}
                      {rosterProviders.map(p => (
                        <div key={p.id} style={s.benchRow}>
                          <span style={s.benchName}>
                            {p.name}
                            <span style={s.benchRole}> · {(p.subRole || "crna").toUpperCase()}</span>
                            {/* Why this idle person is not simply spare: they are
                                holding OB. Quiet chip, never an alarm treatment. */}
                            {standingWatchAhpIds.has(p.id) &&
                              onCallChipLabel(standingWatchRoomByAhpId.get(p.id)) && (
                              <span
                                style={s.benchOnCall}
                                title={onCallChipTitle(standingWatchRoomByAhpId.get(p.id))}
                              >
                                {onCallChipLabel(standingWatchRoomByAhpId.get(p.id))}
                              </span>
                            )}
                            {/* Why this idle person is not spare either: they
                                are walking the break rounds. Same chip shape,
                                break amber, never an alarm treatment. */}
                            {givingBreaksMap[p.id] && (
                              <span style={s.benchGivingBreaks} title="Giving breaks — free, but covering the break rounds">
                                GIVING BREAKS
                              </span>
                            )}
                          </span>
                          {benchCanManage && (
                            <button style={s.benchAssignBtn}
                              onClick={() => setBenchProvider(p)}>
                              Assign →
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })()}

              {/* Home — Full Board AND TV mode. Departed for the day, plus
                  doctors removed from MD Call awaiting Home confirmation. */}
              {!showMyTab && (homeProviders.length > 0 || mdCallHome.length > 0) && (() => {
                const tint = locationTint("__home__", sections.length + 1);
                return (
                  <section style={{ ...s.section, alignSelf: "flex-start", background: tint.bg, border: `1px solid ${tint.border}` }} aria-label="Providers gone home">
                    <div style={{ ...s.sectionHead, background: tint.bg, borderBottom: `1px solid ${tint.border}` }}>
                      <h2 style={{ ...s.sectionTitle, color: tint.head }}>🏠 Home</h2>
                      <span style={{ ...s.sectionCount, color: tint.head, borderColor: tint.border }}>{homeProviders.length + mdCallHome.length}</span>
                    </div>
                    <div style={s.benchList}>
                      {/* Doctors pulled off MD Call (X) — pending confirmation.
                          Back restores them to MD Call on ALL boards; Confirm
                          Home lets them go for the day (roster boxes too). */}
                      {mdCallHome.map((e) => (
                        <div key={`mdcall-${e.code}`} style={s.benchRow}>
                          <span style={s.benchName}>
                            {e.name || e.code}
                            <span style={s.benchRole}> · MD Call</span>
                          </span>
                          {benchCanManage && (
                            <div style={{ display: "flex", gap: space.xs }}>
                              <button style={{ ...s.benchAssignBtn, background: "transparent", color: color.accent.primary, border: `1px solid ${color.accent.primary}` }}
                                title="Restore to MD Call on every board"
                                onClick={() => restoreMdCall(e)}>
                                ↩ Back
                              </button>
                              <button style={{ ...s.benchAssignBtn, background: "transparent", color: color.text.muted, border: `1px solid ${color.border.default}` }}
                                title="Confirm home — off MD Call and every roster box"
                                onClick={() => confirmMdCallHome(e)}>
                                ✓ Confirm home
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      {homeProviders.map(p => (
                        <div key={p.id} style={s.benchRow}>
                          <span style={{ ...s.benchName, color: color.text.muted }}>
                            {p.name}
                            <span style={s.benchRole}> · {p.role === "anesthesiologist" ? "Doctor" : (p.subRole || "crna").toUpperCase()}</span>
                          </span>
                          {benchCanManage && (
                            <div style={{ display: "flex", gap: space.xs }}>
                              <button style={{ ...s.benchAssignBtn, background: "transparent", color: color.accent.primary, border: `1px solid ${color.accent.primary}` }}
                                title="Bring back to Unassigned"
                                onClick={async () => {
                                  try { await setRosterMemberDeparted(facilityId, dateKey, p.id, false, user?.uid); toast.success(`${p.name} is back.`); }
                                  catch (e) { console.error(e); toast.error("Could not bring them back."); }
                                }}>
                                ↩ Back
                              </button>
                              <button style={{ ...s.benchAssignBtn, background: "transparent", color: color.text.muted, border: `1px solid ${color.border.default}` }}
                                title="Confirm home — remove from today's roster"
                                onClick={async () => {
                                  const after = async () => {
                                    await removeRosterMember(facilityId, dateKey, p.id, user?.uid);
                                    toast.success(`${p.name} confirmed home and removed from the roster.`);
                                  };
                                  // Confirming the current AIC home demands a successor first.
                                  if (guardAicHome(p, after)) return;
                                  try { await after(); }
                                  catch (e) { console.error(e); toast.error("Could not remove them from the roster."); }
                                }}>
                                ✓ Confirm home
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })()}

              {/* Traveling — providers in transit between facilities. Shows on
                  BOTH boards; the Arrival button lives on the DESTINATION board
                  (arrival joins them to THIS board's roster → Unassigned). */}
              {!showMyTab && transfers.length > 0 && (() => {
                const tint = locationTint("__traveling__", sections.length + 2);
                const arriveTransfer = async (t) => {
                  try {
                    const members = Array.from(new Map((roster.crnas || []).filter(m => m && m.id).map(m => [m.id, m])).values());
                    if (!members.find((m) => m.id === t.providerId)) {
                      members.push({ id: t.providerId, name: t.providerName, subRole: t.subRole || "crna" });
                    }
                    await setDailyRoster(facilityId, dateKey, { crnas: members }, user?.uid);
                    // Round trip: if they departed THIS facility earlier today,
                    // the stale departed flag would hide them from the bench —
                    // arriving clears it.
                    await setRosterMemberDeparted(facilityId, dateKey, t.providerId, false, user?.uid);
                    await updateTransfer(t.id, { status: "arrived", arrivedAt: new Date().toISOString(), arrivedBy: user?.uid || null });
                    toast.success(`${t.providerName} arrived — now available in Unassigned.`);
                  } catch (e) {
                    console.error("Arrival failed:", e);
                    toast.error("Could not complete the arrival.");
                  }
                };
                return (
                  <section style={{ ...s.section, alignSelf: "flex-start", background: tint.bg, border: `1px solid ${tint.border}` }} aria-label="Providers traveling between facilities">
                    <div style={{ ...s.sectionHead, background: tint.bg, borderBottom: `1px solid ${tint.border}` }}>
                      <h2 style={{ ...s.sectionTitle, color: tint.head }}>🚗 Traveling</h2>
                      <span style={{ ...s.sectionCount, color: tint.head, borderColor: tint.border }}>{transfers.length}</span>
                    </div>
                    <div style={s.benchList}>
                      {transfers.map((t) => (
                        <div key={t.id} style={s.benchRow}>
                          <span style={s.benchName}>
                            {t.providerName}
                            <span style={s.benchRole}>
                              {" "}· {(t.subRole || "crna").toUpperCase()} → {t.toFacilityName || facilityLabel(t.toFacilityId)}
                            </span>
                          </span>
                          {benchCanManage && (
                            <div style={{ display: "flex", gap: space.xs }}>
                              {facilityId === t.toFacilityId && (
                                <button
                                  style={{ ...s.benchAssignBtn, background: "#0a7d5e" }}
                                  title="They're here — join this board's roster"
                                  onClick={() => arriveTransfer(t)}
                                >
                                  ✓ Arrival
                                </button>
                              )}
                              {/* Cancel the trip: back to Unassigned on the ORIGIN board. */}
                              <button
                                style={{ ...s.benchAssignBtn, background: "transparent", color: color.text.muted, border: `1px solid ${color.border.default}` }}
                                title="Cancel the transfer — they return to Unassigned at the origin facility"
                                onClick={async () => {
                                  try {
                                    await updateTransfer(t.id, { status: "cancelled", cancelledAt: new Date().toISOString(), cancelledBy: user?.uid || null });
                                    await setRosterMemberDeparted(t.fromFacilityId, dateKey, t.providerId, false, user?.uid);
                                    // If a round trip left them on the DESTINATION
                                    // roster too, mark them departed there so only
                                    // the origin bench shows them after the cancel.
                                    await setRosterMemberDeparted(t.toFacilityId, dateKey, t.providerId, true, user?.uid);
                                    toast.info(`${t.providerName} is no longer traveling — back in Unassigned at ${facilityLabel(t.fromFacilityId)}.`);
                                  } catch (e) {
                                    console.error("Cancel traveling failed:", e);
                                    toast.error("Could not cancel the transfer.");
                                  }
                                }}
                              >
                                ✕ Cancel Traveling
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })()}
    </>
  );

  return (
    // TV mode pins the whole page to the viewport (height:100vh + clip) so the
    // fit-to-screen measurement has a bounded height to scale into — otherwise
    // tall content grows the page and the board never shrinks.
    <div style={{...s.page, ...(tvMode ? {height:"100vh", overflow:"hidden"} : {})}}>
      {/* Emergency popup — only for a supervisor monitoring MULTIPLE rooms (or
          the AIC/admin who watch the whole board). A doctor in a single case
          room sees the board red + the card Acknowledge, not a center popup. */}
      <IncomingAlertPopup monitorsMultiple={monitorsEmergencyPopup} />

      {previewTomorrow && (
        <div role="status" style={{ background: "#f5f3ff", borderBottom: "2px solid #7c3aed", color: "#5b21b6", fontSize: 13, fontWeight: 700, textAlign: "center", padding: "8px 12px" }}>
          🔭 PREVIEW — tomorrow ({dateKey}). Read-only: this is exactly what goes live at midnight. Edit it from Daily List → Tomorrow.
        </div>
      )}

      {/* TV mode hides all chrome (topbar/banners/stats/filters/tabs) but keeps
          the real board grid — every RoomCard action (status, break, relief,
          remove) stays live. This floating button + Escape exit TV mode. */}
      {tvMode && (
        <div style={s.tvControls}>
          <label style={s.tvScaleCtl} title="Scale the whole board up or down">
            <span aria-hidden="true">A−</span>
            <input
              type="range"
              min="0.6"
              max="1.6"
              step="0.05"
              value={manualScale}
              onChange={(e) => setManualScale(Number(e.target.value))}
              aria-label="Board size"
              style={{ width: 110 }}
            />
            <span aria-hidden="true">A+</span>
          </label>
          {/* 🎤 Voice commands work on the wall display too (own facility only). */}
          {!tvRemoteView && (
            <VoiceControl rooms={rooms} assignments={assignments} bench={rosterProviders} breakGivers={breakGivers} facilityId={facilityId} date={dateKey} />
          )}
          {/* Direct switch to the timeline chart (nav chrome is hidden in TV):
              the Board Manager's second surface, and doctors/AIC/admin asked
              for it on the wall view too (user request 2026-07-08). */}
          {(isBoardManager || isDoctor || isAIC || isAdmin) && (
            <button style={s.tvExit} onClick={() => navigate("/timeline")} aria-label="Open the timeline chart view">📊 Timeline</button>
          )}
          {/* ⚙ TV display settings — three independent, persisted size sliders. */}
          <button
            style={s.tvExit}
            onClick={() => setShowTvSettings((v) => !v)}
            aria-expanded={showTvSettings}
            aria-label="TV display size settings"
            title="Adjust card, font, and panel sizes"
          >
            ⚙ Sizes
          </button>
          <button style={s.tvExit} onClick={() => setTvMode(false)} aria-label="Exit TV mode (press Escape)">✕ Exit TV</button>

          {showTvSettings && (
            <div role="dialog" aria-label="TV display size settings" style={s.tvSettingsPanel}>
              {[
                { key: "card", label: "OR card size", min: 0.7, max: 1.5 },
                { key: "font", label: "Font size in cards", min: 0.7, max: 1.5 },
                { key: "panel", label: "MD Call / CRNA panel size", min: 0.7, max: 3 },
              ].map(({ key, label, min, max }) => (
                <label key={key} style={s.tvSettingsRow}>
                  <span style={s.tvSettingsLabel}>{label}</span>
                  <input
                    type="range"
                    min={min}
                    max={max}
                    step="0.05"
                    value={tvSizes[key]}
                    onChange={(e) => setTvSize(key, Number(e.target.value))}
                    aria-label={label}
                    style={{ width: 150 }}
                  />
                  <span style={s.tvSettingsValue}>{Math.round(tvSizes[key] * 100)}%</span>
                </label>
              ))}
              {/* OR board columns: 1–5 across, or Auto (0) = fit-to-screen heuristic. */}
              <label style={s.tvSettingsRow}>
                <span style={s.tvSettingsLabel}>OR board columns</span>
                <input
                  type="range"
                  min="0"
                  max="5"
                  step="1"
                  value={tvSizes.cols || 0}
                  onChange={(e) => setTvSize("cols", Number(e.target.value))}
                  aria-label="OR board columns (0 = automatic)"
                  style={{ width: 150 }}
                />
                <span style={s.tvSettingsValue}>{tvSizes.cols >= 1 ? tvSizes.cols : "Auto"}</span>
              </label>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                <button
                  style={{ ...s.tvExit, padding: "4px 10px", fontSize: 11 }}
                  onClick={() => { setTvSize("card", 1); setTvSize("font", 1); setTvSize("panel", 1); setTvSize("cols", 0); }}
                >
                  Reset
                </button>
                <button style={{ ...s.tvExit, padding: "4px 10px", fontSize: 11 }} onClick={() => setShowTvSettings(false)}>Done</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TOPBAR (hidden in TV mode) */}
      {!tvMode && (
      <div style={{...s.topbar, ...(isMobile ? {padding:"0 10px"} : {})}}>
        <div style={{...s.topLeft, ...(isMobile ? {gap:6, minWidth:0, flexShrink:1} : {})}}>
          {/* ⚠️ THE LOGO IS THE WAY HOME, AND IT WAS INERT. Every other "go home"
              door in the web app already lands on /home — NavMenu's Home entry,
              the post-login redirect (LoginPage) and the `/` route — but the
              affordance a person reaches for first was two plain spans with
              `cursor: auto`, so clicking it did nothing at all. Owner,
              2026-08-23: *"make sure that everytime that go to home in the web
              app go to the home menu."*

              A LINK, NOT A BUTTON. This is navigation, so it has to survive a
              right-click, a middle-click and a keyboard; an onClick handler on a
              span gives none of those and is the pattern this project's own
              rules forbid.

              ⚠️ AND NOT A LINK ON A WALL DEVICE. `/home` bounces a Board Manager
              straight back to `/board` (App.js, NotBoardManager), so linking it
              there is a door that closes in your face — exactly why NavMenu
              hides its own Home entry for that role. They keep the plain mark. */}
          {isBoardManager ? (
            <span style={s.logo} aria-label="Anesthesia Team Control">
              <span style={s.logoMark} aria-hidden="true">⚕</span>
              {!isMobile && <span style={s.logoWord}>ATC</span>}
            </span>
          ) : (
            <Link
              to="/home"
              style={s.logoLink}
              aria-label="Anesthesia Team Control — go to Home"
            >
              <span style={s.logoMark} aria-hidden="true">⚕</span>
              {!isMobile && <span style={s.logoWord}>ATC</span>}
            </Link>
          )}
          <NavMenu compact={isMobile} />
          {/* Date wraps badly on a narrow topbar and is shown on the Daily List. */}
          {!isMobile && <span style={s.dateChip}>{today}</span>}
        </div>
        <div style={{...s.topRight, ...(isMobile ? {gap:6, minWidth:0, flexShrink:0} : {})}}>
          {/* Emergency + Urgency live in the topbar so they're always reachable.
              Compact (icon-only) on phones so they fit the narrow topbar. */}
          {/* Raise scope (spec R1): AIC/admin may raise for ANY room; a provider
              (CRNA/AA/resident) — and a plain supervising doctor — may only raise
              for the rooms they're assigned to (their My Rooms). So a provider can
              never fire an emergency for a room that isn't theirs. */}
          {!isObserver && (
            <BoardAlertControls
              myRooms={myRooms}
              rooms={isProvider ? myRooms : rooms}
              assignments={assignments}
              compact={isMobile}
              directSingle={isProvider}
            />
          )}
          {/* 🎤 Voice commands — AIC/doctor/BM/admin; confirm-before-execute. */}
          <VoiceControl rooms={rooms} assignments={assignments} bench={rosterProviders} breakGivers={breakGivers} facilityId={facilityId} date={dateKey} />
          {isAIC && !isMobile && <span style={s.aicChip}>AIC</span>}
          <Avatar user={user} isDoctor={isDoctor} onClick={() => navigate("/profile")} />
          {/* Extracted to a shared component (icon-only on phones) so the other
              ten topbars carry the SAME control, sandbox exit included, instead
              of ten copies of it. */}
          <SignOutButton compact={isMobile} />
        </div>
      </div>
      )}

      <div style={s.layout}>
        {/* Navigation now lives in the topbar NavMenu dropdown (no fixed sidebar). */}

        {/* MAIN */}
        {/* overflowY/overflowX (not the `overflow` shorthand) — s.main sets
            overflowY, and React warns when shorthand + longhand mix across
            re-renders (TV toggle). */}
        <div style={{...s.main, ...(tvMode ? {padding:"14px 18px", overflowY:"hidden", overflowX:"hidden", display:"flex", flexDirection:"column", minHeight:0} : {})}}>
          {/* TV-mode header: location + room count on the left, today's AIC on
              the right of the same line (e.g. "MAIN OR · 12        AIC: …"). */}
          {tvMode && (
            <div style={s.tvHeader}>
              {/* Upper-left: TV-only facility switcher (view override — never
                  writes activeFacilityId) + locations shown + count + AIC. */}
              <span style={{ display: "flex", alignItems: "baseline", gap: 14, minWidth: 0 }}>
                {tvFacilityOptions.length > 1 && (
                  <select
                    value={facilityId || ""}
                    onChange={(e) =>
                      setTvFacilityId(e.target.value === user?.activeFacilityId ? null : e.target.value)
                    }
                    style={s.tvFacilitySelect}
                    aria-label="Show facility on this TV"
                  >
                    {tvFacilityOptions.map((f) => {
                      const known = facilityLabel(f.id);
                      const label = known !== f.id ? known : f.label || f.name || f.id;
                      return (
                        <option key={f.id} value={f.id}>{shortFacilityLabel(label)}</option>
                      );
                    })}
                  </select>
                )}
                <span style={s.tvHeaderAic}>
                  {locFilter === "all" ? "All Locations" : labelFor(locations, locFilter)}
                  {" · "}{visibleRooms.length} rooms
                  {roster.aicName ? `  ·  AIC: ${roster.aicName}` : ""}
                </span>
              </span>
              {/* Right: the current facility name — always shown, for every role. */}
              <span style={s.tvHeaderLoc}>{facilityName}</span>
            </div>
          )}

          {/* AIC / Administrator banner — full board control (hidden in TV mode). */}
          {!tvMode && (isAIC || isAdmin) && (
            <div style={s.aicBanner}>
              <div>
                <div style={s.bannerTitleAmber}>
                  {isAdmin ? "You are the Administrator" : "You are the Anesthesiologist In Charge"}
                </div>
                <div style={s.bannerBody}>
                  {isAdmin
                    ? "Full board control. You can manage everything on the board."
                    : "Full board control. Non-AIC changes notify you."}
                </div>
              </div>
              <div style={{display:"flex",gap:space.sm,flexWrap:"wrap"}}>
                {SHOW_SEED_TOOLS && rooms.length === 0 && <button style={{...s.btnPrimary, background:color.accent.active}} onClick={() => runSeed(seedDatabase, "Rooms")}>🌱 Setup Rooms</button>}
                <button style={s.btnPrimary} onClick={() => setShowAssign(true)}>＋ Add Assignment</button>
                <button style={{...s.btnPrimary, background:"#0891b2"}} onClick={() => setShowAddProvider(true)}>＋ Add Provider</button>
              </div>
            </div>
          )}

          {/* Supervising Doctor banner — AIC is notified of changes (hidden in TV mode). */}
          {!tvMode && isDoctor && !isAIC && !isAdmin && (
            <div style={s.supBanner}>
              <div>
                <div style={s.bannerTitleNavy}>You are a Supervising Doctor</div>
                <div style={s.bannerBody}>
                  You oversee your rooms and can update assignments. The AIC is notified of every change.
                </div>
              </div>
              {canManageAssignments && (
                <button style={{...s.btnPrimary, background:color.accent.primary}} onClick={() => setShowAssign(true)}>
                  ＋ Add / Change Assignment
                </button>
              )}
            </div>
          )}

          {/* Break Requests approval queue removed — requests go straight to
              the board as the yellow break-needed card (cover from the card). */}

          {/* Board header — wraps on phones so the Emergency/Urgency controls
              drop below the room count instead of clipping off the edge.
              Hidden in TV mode. */}
          {!tvMode && (
          <div style={{...s.boardHeader, ...(isMobile ? {flexWrap:"wrap", gap:10} : {})}}>
            <div style={{display:"flex", alignItems:"center", gap:10}}>
              <div>
                {/* Always render the page's h1 — visually hidden on phones so
                    screen-reader navigation keeps a top-level heading. */}
                <h1 style={isMobile ? s.srOnly : s.pageTitle}>OR Board</h1>
                {/* Board layout selector. ADDITIVE ONLY — this board's own
                    content and behaviour are unchanged; the three alternatives
                    are being trialled alongside it, and this is how you reach
                    them and come back. */}
                {!isMobile ? (
                  <div style={{ marginTop: 4 }}>
                    <BoardSwitcher />
                  </div>
                ) : null}
                <div style={{fontSize:13, color:color.text.muted, fontVariantNumeric:"tabular-nums"}}>Live · {locRooms.length} rooms{locFilter !== "all" ? ` · ${labelFor(locations, locFilter)}` : ""}</div>
              </div>
              {isObserver && <span style={s.viewOnlyChip}>View only</span>}
            </div>
            {/* Emergency/Urgency now live in the topbar (top-right). TV mode is
                available on the Full Board view for everyone (incl. observers). */}
            <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
              {!showMyTab && (
                <>
                {canManageAssignments && (
                  <button
                    style={{ ...s.tvBtn, ...(previewTomorrow ? { background: "#7c3aed", color: "#fff", borderColor: "#7c3aed" } : {}) }}
                    onClick={() => setPreviewTomorrow((v) => !v)}
                    aria-pressed={previewTomorrow}
                    title="Preview tomorrow's staged board (read-only) — it goes live automatically at midnight"
                  >
                    {previewTomorrow ? "← Back to Today" : "🔭 Preview Tomorrow"}
                  </button>
                )}
                <button style={s.tvBtn} onClick={() => setTvMode(true)} title="Full-screen TV mode — clean board for a wall display">
                  📺 TV Mode
                </button>
                {/* Quick jump to the timeline chart, right by TV Mode
                    (user request 2026-07-08). */}
                <button style={s.tvBtn} onClick={() => navigate("/timeline")} title="Gantt view of today's cases by room">
                  📊 Timeline Chart
                </button>
                </>
              )}
            </div>
          </div>
          )}

          {/* My Rooms / Full Board tabs — not shown to observers (D-10); hidden in TV mode. */}
          {!tvMode && hasMyRooms && (
            <RoleTab
              tabs={[{id:"my",label:"My Rooms"},{id:"full",label:"Full Board"}]}
              value={currentTab}
              onChange={setCurrentTab}
            />
          )}

          {/* Status summary cards + filter pills removed per request — the board
              shows occupied rooms; live state reads off each card. */}

          {/* Grid (TV mode: tvFitRef is the box; the board is a fluid grid that
              fills it — full width and height, on any TV) */}
          {/* The ONE tabpanel container RoleTab's aria-controls points at —
              this id used to be stamped on every location section (duplicate
              DOM ids, invalid + confusing to AT). */}
          <div ref={tvFitRef} id={`tabpanel-${currentTab}`} role={hasMyRooms && !tvMode ? "tabpanel" : undefined} style={tvMode ? s.tvFit : undefined}>
          {roomsError ? (
            <div role="alert" style={{textAlign:"center", padding:"40px 20px", color:color.accent.error}}>
              <div style={{fontSize:15, fontWeight:700, marginBottom:6}}>The board couldn't load.</div>
              <div style={{fontSize:13, color:color.text.muted}}>Check your connection, then reload. If it keeps happening, sign out and back in.</div>
            </div>
          ) : loading ? (
            <div style={{color:color.text.muted, textAlign:"center", padding:40}}>Loading rooms…</div>
          ) : rooms.length === 0 ? (
            <div style={{textAlign:"center", padding:"60px 20px", color:color.text.muted}}>
              <div style={{fontSize:40, marginBottom:12}}>🏥</div>
              <div style={{fontSize:16, fontWeight:700, marginBottom:8, color:color.text.primary}}>No rooms yet</div>
              <div style={{fontSize:13, marginBottom:20}}>Add rooms in Setup to start your OR day.</div>
            </div>
          ) : (
            <div ref={tvMode ? tvBoardRef : undefined}
                 style={{...(tvMode ? s.tvBoardRow : s.board), ...(tvMode ? { transform:`scale(${tvScale})`, transformOrigin:"top center" } : {}),
                 // With bench sections the column must take its NATURAL height
                 // (not stretch to the screen) so scrollHeight is honest and the
                 // fit-to-screen scale shrinks rooms + benches together.
                 ...(tvMode && hasTvBenches ? { alignSelf: "flex-start" } : {})}}>
              {/* Rooms area. Non-TV: display:contents so sections stay direct flex
                  children of s.board (layout unchanged). TV: ONE flat uniform grid
                  of every room (location shows on each card) so a one-room
                  location never wastes a full-width row. */}
              <div style={tvMode ? s.tvRoomsArea : s.contents}>
              {tvMode ? (
                tvFlatRooms.length === 0 ? (
                  <div style={{width:"100%", textAlign:"center", padding:"40px 20px", color:color.text.muted, fontSize:13}}>No rooms to show.</div>
                ) : (
                  <div style={{ ...roomAreaStyle, zoom: tvSizes.card }}>
                    {tvFlatRooms.map(room => {
                      const t = tvTintByRoom.get(room.id);
                      return (
                        <div key={room.id} style={{
                          ...cardWrapStyle,
                          // A clearly-visible location-coloured mat behind each
                          // white card (same palette as the board sections): the
                          // tint fill + a 2px coloured frame + a thicker top bar
                          // in the location's heading colour so a wall viewer
                          // tells locations apart at a glance.
                          ...(t ? { background: t.bg, border: `2px solid ${t.border}`, borderTop: `5px solid ${t.head}`, borderRadius: radius.lg, padding: 8 } : {}),
                        }}>{renderRoomCard(room)}</div>
                      );
                    })}
                    {benchSectionsJsx}
                  </div>
                )
              ) : sections.length === 0 ? (
                <div style={{width:"100%", textAlign:"center", padding:"40px 20px", color:color.text.muted, fontSize:13}}>
                  {showMyTab
                    ? "No rooms assigned to you today."
                    : "No rooms match this filter."}
                </div>
              ) : sections.map((section, sectionIdx) => {
                const tint = locationTint(section.key, sectionIdx);
                return (
                <section key={section.key}
                  style={{ ...s.section, background: tint.bg, border: `1px solid ${tint.border}` }}
                  aria-label={section.label}>
                  <div style={{ ...s.sectionHead, background: tint.bg, borderBottom: `1px solid ${tint.border}` }}>
                    <h2 style={{ ...s.sectionTitle, color: tint.head }}>{section.label}</h2>
                    <span style={{ ...s.sectionCount, color: tint.head, borderColor: tint.border }}>{section.rooms.length}</span>
                  </div>
                  <div style={roomAreaStyle}>
                    {section.rooms.map(room => (
                      <div key={room.id} style={cardWrapStyle}>{renderRoomCard(room)}</div>
                    ))}
                  </div>
                </section>
                );
              })}

              {/* Bench boxes: on the standard board they FLOW after the
                  location sections (display:contents); in TV they render INSIDE
                  the rooms grid so they continue the last row of cards. */}
              {!tvMode && <div style={{ display: "contents" }}>{benchSectionsJsx}</div>}
              </div>
              {/* TV info panels (Prompt 2/3): MD Call, CRNA by shift, Broadcast. */}
              {tvMode && (
                <div ref={tvPanelsRef} style={{ ...s.tvPanels, zoom: tvSizes.panel }}>
                  <TvCallPanel items={tvMdCall} callList={tvMdCallList.length ? tvMdCallList : null} bodyMaxHeight="30vh" onRemove={canManageAssignments ? removeMdCall : null} facilityBoards={facilityBoards} facilities={allFacilities} />
                  <TvCrnaPanel groups={tvCrnaGroups} bodyMaxHeight="26vh" />
                  {/* Unassigned now renders as a full bench section at the
                      bottom of the TV board (with Assign buttons) — the old
                      read-only side panel duplicated it. */}
                  <TvBroadcastPanel
                    broadcasts={broadcasts}
                    canWrite={isAIC}
                    facilityId={facilityId}
                    dateKey={dateKey}
                    author={{ id: user?.uid, name: user?.name }}
                    bodyMaxHeight="20vh"
                  />
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Bench Assign chooser → New Room (simplified modal) / Takeover / Home. */}
      <BenchAssignFlow
        provider={benchProvider}
        rooms={rooms}
        assignments={assignments}
        facilityId={facilityId}
        date={dateKey}
        updatedBy={user?.uid}
        // The same clock this page's own Unassigned list uses (`rowClaimsAt`
        // above), so the menu cannot contradict the list it was opened from.
        nowMin={nowMinutes()}
        onNewRoom={(pid) => { setBenchProvider(null); setAssignProviderId(pid); setSimplifiedAssign(true); setShowAssign(true); }}
        onClose={() => setBenchProvider(null)}
      />

      {/* Unassigned DOCTOR chooser → Back to MD Call (when X-removed) /
          New Room (Room+MD) / Takeover / Home. */}
      <DoctorAssignFlow
        doctor={benchDoctor}
        rooms={rooms}
        assignments={assignments}
        facilityId={facilityId}
        date={dateKey}
        updatedBy={user?.uid}
        onNewRoom={(did) => { setBenchDoctor(null); setAssignAttendingId(did); setDoctorAssignMode(true); setShowAssign(true); }}
        onClose={() => setBenchDoctor(null)}
        mdCallPending={mdCallPendingFor(benchDoctor)}
        onBackToCall={restoreMdCall}
        onAfterHome={confirmMdCallHome}
        // Sending the current AIC home from the chooser routes through the
        // handoff modal instead (successor picked first).
        onHomeIntercept={(doc) =>
          guardAicHome(doc, async () => {
            await setRosterMemberDeparted(facilityId, dateKey, doc.id, true, user?.uid);
          })
        }
      />

      {/* AIC handoff — interposes whenever a board flow sends the current AIC
          home. Successor gets full AIC control automatically (roster-driven). */}
      {aicHandoff && (
        <AicHandoffModal
          person={aicHandoff.person}
          doctors={availableRosterDoctors.filter((d) => !departedMap[d.id])}
          onConfirm={confirmAicHandoff}
          onClose={() => setAicHandoff(null)}
          busy={aicHandoffBusy}
        />
      )}

      {/* Add Provider — pull a doctor/CRNA from another facility onto today's
          roster, asking their call number / leave time at add time. */}
      <CrossFacilityPicker
        isOpen={showAddProvider}
        onClose={() => setShowAddProvider(false)}
        currentFacilityId={facilityId}
        date={dateKey}
        actor={realUser}
        roster={roster}
        localPool={[...doctorPool, ...providerPool]}
        floatAllowed={facilityRules.floatAllowed || isAdmin}
      />

      {/* COMBINED ADD ROOM + ASSIGNMENT MODAL */}
      {showAssign && (
        <AssignmentModal
          presetProviderId={assignProviderId}
          presetAttendingId={assignAttendingId}
          simplified={simplifiedAssign}
          doctorMode={doctorAssignMode}
          onClose={() => { setShowAssign(false); setAssignProviderId(null); setSimplifiedAssign(false); setDoctorAssignMode(false); setAssignAttendingId(null); }}
          rooms={rooms}
          locations={locations}
          activeLocs={activeLocs}
          rosterEmpty={rosterEmpty}
          doctors={rosterDoctors}
          providers={rosterProvidersAll}
          assignments={assignments}
          facilityId={facilityId}
          mustNotifyAIC={mustNotifyAIC}
          realUser={realUser}
          onCreateList={() => navigate("/daily-list")}
          onSetCallOrder={handleSetCallOrder}
        />
      )}

    </div>
  );
}

// ── Styles (light token theme — D-13) ─────────────
const s = {
  page:        {background:color.surface.base, color:color.text.primary, minHeight:"100vh", fontFamily:font.family, display:"flex", flexDirection:"column"},
  topbar:      {background:color.surface.card, borderBottom:`1px solid ${color.border.default}`, padding:"0 20px", height:56, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0, position:"sticky", top:0, zIndex:10},
  topLeft:     {display:"flex", alignItems:"center", gap:14},
  topRight:    {display:"flex", alignItems:"center", gap:10},
  logo:        {display:"inline-flex", alignItems:"center", gap:8, whiteSpace:"nowrap"},
  // Same box as `logo`, plus the things a link needs: no underline, an
  // inherited colour (the mark and the word carry their own), and a pointer
  // so the affordance reads as clickable before it is clicked. The 28px
  // logoMark already clears the 24px SC 2.5.8 target on its own.
  logoLink:    {display:"inline-flex", alignItems:"center", gap:8, whiteSpace:"nowrap", textDecoration:"none", color:"inherit", cursor:"pointer"},
  logoMark:    {width:28, height:28, borderRadius:8, background:color.accent.primary, color:"#fff", display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:font.weight.bold, flexShrink:0},
  logoWord:    {fontSize:font.size.heading, fontWeight:font.weight.bold, color:color.accent.primary, letterSpacing:"-0.01em"},
  facilityChip:{background:color.surface.raised, borderRadius:radius.full, padding:"3px 12px", fontSize:font.size.label, color:color.text.secondary},
  locSelect:   {background:color.surface.raised, border:`1px solid ${color.border.default}`, borderRadius:radius.full, padding:"3px 12px", fontSize:font.size.label, color:color.text.secondary, fontFamily:"inherit", cursor:"pointer"},
  dateChip:    {fontSize:font.size.label, color:color.text.muted},
  aicChip:     {background:"#f59e0b20", border:"1px solid #f59e0b", color:"#7a4f00", borderRadius:radius.full, padding:"3px 10px", fontSize:font.size.label, fontWeight:font.weight.bold},
  avatar:      {width:32, height:32, borderRadius:"50%", background:color.accent.primary, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:font.weight.bold, fontSize:font.size.label, color:"#fff"},
  layout:      {display:"flex", flex:1, overflow:"hidden"},
  sidebar:     {width:200, background:color.surface.card, borderRight:`1px solid ${color.border.default}`, padding:"12px 0", flexShrink:0},
  navItem:     {display:"flex", alignItems:"center", gap:10, padding:"8px 16px", cursor:"pointer", color:color.text.secondary, fontSize:font.size.body, background:"transparent", border:"none", width:"100%", textAlign:"left", fontFamily:"inherit"},
  navActive:   {background:"#1e40af10", color:color.accent.primary, borderRight:`3px solid ${color.accent.primary}`},
  navLabel:    {padding:"16px 16px 6px", fontSize:font.size.label, fontWeight:font.weight.bold, color:color.text.muted, letterSpacing:"1.2px", textTransform:"uppercase"},
  main:        {flex:1, overflowY:"auto", padding:"22px 24px"},
  aicBanner:   {background:"#f59e0b0a", border:`1px solid #f59e0b44`, borderLeft:"4px solid #f59e0b", borderRadius:radius.md, padding:"14px 18px", marginBottom:18, display:"flex", alignItems:"center", justifyContent:"space-between"},
  supBanner:   {background:"#1e40af0a", border:`1px solid #1e40af44`, borderLeft:`4px solid ${color.accent.primary}`, borderRadius:radius.md, padding:"14px 18px", marginBottom:18, display:"flex", alignItems:"center", justifyContent:"space-between"},
  bannerTitleAmber:{fontWeight:font.weight.bold, fontSize:font.size.body, color:"#b45309"},
  bannerTitleNavy: {fontWeight:font.weight.bold, fontSize:font.size.body, color:color.accent.primary},
  bannerBody:  {fontSize:font.size.label, color:color.text.muted, marginTop:2},
  panel:       {background:color.surface.card, border:`1px solid ${color.border.default}`, boxShadow:shadow.sm, borderRadius:radius.md, padding:16, marginBottom:18},
  panelTitle:  {fontSize:14, fontWeight:font.weight.bold, marginBottom:14, display:"flex", alignItems:"center", gap:8, color:color.text.primary},
  badge:       {background:"#f97316", color:"#fff", borderRadius:10, padding:"1px 7px", fontSize:10, fontWeight:font.weight.bold},
  breakRow:    {display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${color.border.default}`},
  approveBtn:  {background:"#e6f8f3", border:"1px solid #10b981", color:"#0a7d5e", borderRadius:radius.sm, padding:"3px 8px", fontSize:11, cursor:"pointer"},
  denyBtn:     {background:"#fdecec", border:"1px solid #dc2626", color:"#b91c1c", borderRadius:radius.sm, padding:"3px 8px", fontSize:11, cursor:"pointer"},
  boardHeader: {display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18},
  viewOnlyChip:{fontSize:font.size.label, color:color.text.muted, background:color.surface.raised, border:`1px solid ${color.border.default}`, borderRadius:radius.full, padding:"3px 10px"},
  pageTitle:   {fontSize:font.size.display, fontWeight:font.weight.bold, margin:0, color:color.text.primary, letterSpacing:"-0.02em"},
  // Visually-hidden but screen-reader-visible (the standard sr-only clip).
  srOnly:      {position:"absolute", width:1, height:1, padding:0, margin:-1, overflow:"hidden", clip:"rect(0 0 0 0)", whiteSpace:"nowrap", border:0},
  btnPrimary:  {background:color.accent.primary, color:"#fff", border:"none", borderRadius:radius.sm, padding:"7px 14px", fontSize:font.size.body, fontWeight:font.weight.bold, cursor:"pointer"},
  tvBtn:       {background:color.surface.raised, color:color.text.secondary, border:`1px solid ${color.border.default}`, borderRadius:radius.full, padding:"6px 14px", fontSize:font.size.body, fontWeight:font.weight.bold, cursor:"pointer", whiteSpace:"nowrap"},
  tvControls:  {position:"fixed", bottom:14, right:14, zIndex:1100, display:"flex", alignItems:"center", gap:10},
  // ⚙ size-settings popover, anchored above the TV control strip.
  tvSettingsPanel: {position:"absolute", bottom:"calc(100% + 10px)", right:0, width:300, background:color.surface.card, border:`1px solid ${color.border.default}`, borderRadius:radius.md, boxShadow:shadow.lg, padding:14, display:"flex", flexDirection:"column", gap:10},
  tvSettingsRow: {display:"flex", alignItems:"center", gap:10},
  tvSettingsLabel: {flex:1, fontSize:12, fontWeight:font.weight.bold, color:color.text.primary},
  tvSettingsValue: {width:38, textAlign:"right", fontSize:11, color:color.text.muted, fontVariantNumeric:"tabular-nums"},
  tvScaleCtl:  {display:"flex", alignItems:"center", gap:6, background:color.surface.card, border:`1px solid ${color.border.default}`, borderRadius:radius.full, padding:"5px 12px", fontSize:12, fontWeight:font.weight.bold, color:color.text.secondary, boxShadow:shadow.md, opacity:.94},
  tvExit:      {background:color.accent.primary, color:"#fff", border:"none", borderRadius:radius.full, padding:"8px 16px", fontSize:font.size.body, fontWeight:font.weight.bold, cursor:"pointer", boxShadow:shadow.md, opacity:.92},
  // TV fit area: the usable box under the header. The board inside is a fluid
  // grid that fills it (width + height) with no transform, so there are no
  // empty side or bottom bands on any TV.
  // Stretch (not center) so the board fills the whole usable height — the grid
  // rows grow to use the space instead of leaving an empty band on a big screen.
  tvFit:       {flex:1, minHeight:0, width:"100%", overflow:"hidden", display:"flex", alignItems:"stretch", justifyContent:"center"},
  // display:contents makes the rooms wrapper transparent in NON-TV mode so the
  // location sections remain direct flex children of s.board (layout unchanged).
  contents:    {display:"contents"},
  // TV layout: [rooms area | info-panels column] filling the whole box. The
  // rooms area takes all width left of the panel rail; the rail is a screen-
  // proportional width so it scales with the TV.
  tvBoardRow:  {display:"flex", gap:14, width:"100%", alignItems:"stretch"},
  tvRoomsArea: {display:"flex", flexDirection:"column", gap:14, flex:1, minWidth:0},
  tvPanels:    {display:"flex", flexDirection:"column", gap:10, width:"clamp(280px, 19vw, 440px)", flexShrink:0, minHeight:0, overflowY:"auto"},
  tvHeader:    {display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:16, padding:"2px 56px 12px 2px", marginBottom:6, borderBottom:`1px solid ${color.border.default}`, flexShrink:0},
  tvHeaderLoc: {fontSize:"clamp(20px,2.4vw,40px)", fontWeight:font.weight.bold, color:color.text.primary, textTransform:"uppercase", letterSpacing:".5px"},
  tvHeaderAic: {fontSize:"clamp(14px,1.6vw,26px)", fontWeight:font.weight.bold, color:color.accent.primary, whiteSpace:"nowrap"},
  // TV-only facility switcher (upper-left). Big enough to hit from a couch
  // remote / wall-mounted touch panel; never rendered off TV mode.
  tvFacilitySelect: {fontSize:"clamp(14px,1.5vw,24px)", fontWeight:font.weight.bold, padding:"6px 10px", borderRadius:radius.sm, border:`1px solid ${color.border.default}`, background:color.surface.card, color:color.text.primary, fontFamily:"inherit", cursor:"pointer", maxWidth:"40vw"},
  btnDanger:   {background:color.accent.error, color:"#fff", border:"none", borderRadius:radius.sm, padding:"9px 16px", fontSize:font.size.body, fontWeight:font.weight.bold, cursor:"pointer"},
  statsRow:    {display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:22, marginTop:18},
  statCard:    {background:color.surface.card, boxShadow:shadow.sm, borderRadius:radius.md, padding:16},
  filterBar:   {display:"flex", gap:8, marginBottom:16, flexWrap:"wrap"},
  filterPill:  {padding:"7px 14px", borderRadius:radius.full, border:`1px solid ${color.border.default}`, background:"transparent", color:color.text.secondary, fontSize:font.size.body, cursor:"pointer", whiteSpace:"nowrap"},
  filterActive:{background:color.accent.primary, border:`1px solid ${color.accent.primary}`, color:"#fff"},
  board:       {display:"flex", flexWrap:"wrap", gap:14, width:"100%", alignItems:"flex-start"},
  // A location is a soft-tinted rounded panel. flex:"0 1 auto" sizes it to its
  // rooms; a panel wider than the row shrinks and its cards wrap (full width),
  // while narrow panels pack side by side. bg/border are applied per location.
  section:     {flex:"0 1 auto", maxWidth:"100%", minWidth:232, borderRadius:radius.md, padding:"6px 12px 12px", boxSizing:"border-box"},
  sectionHead: {display:"flex", alignItems:"center", gap:10, marginBottom:10, padding:"6px 2px", borderRadius:radius.sm},
  sectionTitle:{fontSize:font.size.label, fontWeight:font.weight.bold, color:color.text.primary, textTransform:"uppercase", letterSpacing:"1px", margin:0},
  sectionCount:{background:color.surface.raised, border:`1px solid ${color.border.default}`, color:color.text.muted, borderRadius:radius.full, padding:"1px 9px", fontSize:font.size.label, fontWeight:font.weight.bold, fontVariantNumeric:"tabular-nums"},
  column:      {display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(208px,1fr))", gap:10, alignItems:"stretch"},
  // Bench sections (Unassigned / Home / Traveling): a narrow vertical panel of
  // full-width white rows — bold name · muted role on the left, action button
  // on the right. The panel's height simply follows the people count
  // (user-preferred layout, screenshot 2026-07-07). Same on board and TV.
  benchArea:   {display:"flex", flexWrap:"wrap", gap:12, alignItems:"flex-start", alignSelf:"stretch", width:"100%"},
  benchList:   {display:"flex", flexDirection:"column", gap:8, minWidth:0, width:"100%", maxWidth:340},
  benchRow:    {display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, background:color.surface.card, border:`1px solid ${color.border.default}`, borderRadius:10, padding:"10px 12px", boxShadow:shadow.sm},
  benchName:   {fontSize:font.size.body, fontWeight:font.weight.bold, color:color.text.primary, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"},
  benchRole:   {fontSize:font.size.label, fontWeight:font.weight.regular, color:color.text.muted},
  // Teal, outlined, no fill — the same treatment FREE NOW uses for this chip.
  // It marks a duty, not a problem; red and blink stay spent on emergencies.
  benchOnCall: {marginLeft:6, fontSize:10, fontWeight:font.weight.bold, letterSpacing:".4px", color:"#0f766e", border:"1px solid #0f766e", borderRadius:radius.full, padding:"1px 6px", whiteSpace:"nowrap"},
  // Same shape as benchOnCall in the board's break amber (#7a4f00 ink — the raw
  // #f59e0b fails AA on white). Two chips of the same KIND must read as the
  // same kind of thing.
  benchGivingBreaks: {marginLeft:6, fontSize:10, fontWeight:font.weight.bold, letterSpacing:".4px", color:"#7a4f00", border:"1px solid #f59e0b", borderRadius:radius.full, padding:"1px 6px", whiteSpace:"nowrap"},
  benchAssignBtn:{background:color.accent.primary, border:"none", color:"#fff", borderRadius:radius.sm, padding:"5px 12px", fontSize:font.size.label, fontWeight:font.weight.bold, cursor:"pointer", whiteSpace:"nowrap"},
  overlay:     {position:"fixed", inset:0, background:"rgba(0,0,0,.40)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:16},
  modal:       {background:color.surface.card, border:`1px solid ${color.border.default}`, boxShadow:shadow.lg, borderRadius:radius.lg, padding:26, width:440, maxWidth:"95vw"},
  modalTitle:  {fontSize:font.size.heading, fontWeight:font.weight.bold, marginBottom:4, color:color.text.primary},
  modalSub:    {fontSize:13, color:color.text.muted, marginBottom:20},
  modalActions:{display:"flex", gap:10, justifyContent:"flex-end", marginTop:20},
  outlineBtn:  {background:"transparent", border:`1px solid ${color.border.default}`, color:color.text.secondary, borderRadius:radius.sm, padding:"8px 16px", fontSize:13, cursor:"pointer"},
};
